import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Play, Pause } from "lucide-react";

/**
 * Floating ambient music control.
 * Strategy: starts muted-autoplay immediately (browsers allow this),
 * then unmutes + raises volume the instant the user makes ANY interaction
 * (click, tap, scroll, key, touch) — so it effectively "auto plays with sound"
 * after the very first action on the page. Persists state in sessionStorage
 * so unmute only triggers once per browser session.
 */
const SESSION_KEY = "ironclad-audio-unmuted";

export default function MusicToggle({ src = "/audio/ironclad-hero.mp3?v=12" }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.35;
    a.loop = true;
    a.muted = true;
    a.play().then(() => setPlaying(true)).catch(() => {});

    // The instant the user does ANYTHING on the page, unmute & play with sound.
    const unmute = async () => {
      try {
        a.muted = false;
        await a.play();
        setMuted(false);
        setPlaying(true);
        try {
          sessionStorage.setItem(SESSION_KEY, "1");
        } catch {}
      } catch {}
      cleanup();
    };

    const events = ["pointerdown", "click", "touchstart", "keydown", "scroll", "wheel"];
    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, unmute));
    };
    events.forEach((e) =>
      window.addEventListener(e, unmute, { once: false, passive: true })
    );

    return cleanup;
  }, []);

  const toggleMute = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (muted) {
      a.muted = false;
      setMuted(false);
      try {
        await a.play();
        setPlaying(true);
      } catch {}
    } else {
      a.muted = true;
      setMuted(true);
    }
  };

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      try {
        a.muted = false;
        await a.play();
        setMuted(false);
        setPlaying(true);
      } catch {}
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  return (
    <>
      <audio ref={audioRef} src={src} preload="auto" />
      <div
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2"
        data-testid="music-toggle"
      >
        <button
          onClick={toggleMute}
          className="w-12 h-12 bg-black/80 backdrop-blur-md border border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid="music-mute-btn"
        >
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
        <button
          onClick={togglePlay}
          className="hidden sm:flex w-12 h-12 bg-black/80 backdrop-blur-md border border-border text-foreground hover:border-primary hover:text-primary transition-colors items-center justify-center"
          aria-label={playing ? "Pause" : "Play"}
          data-testid="music-play-btn"
        >
          {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        </button>
      </div>
    </>
  );
}
